/**
 * @voltxampere/web — Boot fixture (Sprint 19 Functional Pass)
 *
 * Sprint 17 hotfix re-exported the layout-agnostic
 * `voltageDivider` preset from `@voltxampere/presets`. The
 * preset was electrically correct but visually broken: every
 * component anchored at (0, 0), every `wires` array empty.
 * The user opened the app and saw a stack of overlapping
 * symbols with no rails — F5 / DC ran but produced no visible
 * circuit.
 *
 * Sprint 19 (ADR-0036) splits the boot fixture from the preset
 * library. The preset still exists for the DEVRELER pane (15
 * thumbnails, layout-agnostic per Sprint 22+ scope) and for
 * test reproducibility (engine sentinel `V_mid = 5.0` lives in
 * `packages/engine/tests/regression/sprint-5-7-numerical.test.ts`
 * with its own in-test fixture, NOT this one). The boot fixture
 * is now hand-built to match `docs/design/zenit-platform.html`
 * lines 2070-2135 birebir:
 *
 *   V1  @ (200, 140)  12 V       voltage source, vertical
 *   R1  @ (440, 150)  10 kΩ      resistor, rotation 90 (vertical)
 *   R2  @ (440, 310)  4.7 kΩ     resistor, rotation 90 (vertical)
 *   GND @ (320, 440)              ground symbol
 *
 *   wire_vcc:  (200,112) → (200,70) → (440,70)         (V1+ → R1.p1)
 *   wire_gnd:  (200,168) → (200,440) → (320,440)
 *              → (440,440) → (440,390)                  (V1− and R2.p2 share rail; GND junction in middle)
 *
 * Mockup parity values (`R1=10k`, `R2=4.7k`, `V1=12`) yield the
 * analytical voltage at the n2 measurement node:
 *   V_mid = 12 × 4.7 / (10 + 4.7) = 3.8367 V ≈ +3.84 V
 *
 * Sprint 80.X "Sandık Kapağı" (F-V-200, ADR-0118) updated the
 * landing hero SVG label from the legacy "+8.245V" mockup
 * placeholder to "+3.84V" so the marketing surface matches the
 * solver output. The boot DC solve reports ~3.84 V at net_mid;
 * the inspector tile + waveform trace + landing hero schematic
 * now all agree. Engine sentinel (V_mid=5.0 with R1=R2=1k) is a
 * separate fixture; this fixture's expected value is documented
 * in the boot test.
 *
 * The DEVRELER pane's voltage-divider thumbnail still uses the
 * preset (1k/1k → 5 V), so the preset's pedagogical contract is
 * preserved — only the boot fixture diverges, and only for
 * canonical-mockup parity reasons.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Circuit, ComponentInstance, Net, Wire } from "@voltxampere/schema";

/**
 * Frozen authoring timestamp (2026-04-27). Boot fixture is
 * deterministic — keeps schema snapshot tests stable.
 */
const FROZEN_TIMESTAMP = 1_745_712_000_000;

/** Schema pin (matches preset library). */
const SCHEMA_VERSION = "0.4.0";

const nets: Net[] = [
  { id: "net_gnd", name: "gnd", isGround: true },
  { id: "net_vcc", name: "vcc", isGround: false },
  { id: "net_mid", name: "mid", isGround: false },
];

// Sprint 19 hotfix-3 — pin-snapped layout.
//
// The Sprint 17 mockup-canonical positions (V1@(200,140) 12V circle
// r=28, R1@(440,70..230) 160px tall body, etc.) assumed symbols that
// scale with their schematic size. Our renderer uses fixed
// `viewBox: 0 0 40 26` for every symbol — the pins on a vertical
// resistor are 40 world units apart, NOT 160. With the mockup-
// canonical wire path the wire endpoints sat 60+ pixels away from
// the actual symbol pin world coordinates, so the canvas painted a
// "circuit" where wires hovered 60px above resistor terminals.
//
// This pass re-derives every fixture position from the actual
// symbol pin geometry so pin world coords match wire endpoints
// exactly. Layout is compact (~80px tall instead of mockup's
// ~370px) but every connection visually closes. Mockup parity
// returns when Sprint 22+ scales symbols up to match the mockup
// pin-spacing.
//
// Pin world derivation (verified empirically with Canvas2D
// rotate semantics — positive angle CW, anchor-then-rotate):
//
//   dc-source (rot=0)      pos=(anchor.x, anchor.y-10), neg=(anchor.x, anchor.y+10)
//   resistor (rot=90)      p1=(anchor.x, anchor.y-20), p2=(anchor.x, anchor.y+20)
//                          [terminals[0]=local(0,13)→world top after CW rotation]
//   ground (rot=0)         p=(anchor.x, anchor.y-9)
//
// Layout target:
//   vcc rail at y=130 (V1.pos = R1.p1 = wire endpoint)
//   junction at y=170 (R1.p2 = R2.p1)
//   gnd rail at y=210 (R2.p2 = GND.p = wire endpoint)
//
// Therefore:
//   V1   anchor (200, 140) → pos=(200,130) neg=(200,150)
//   R1   anchor (440, 150) → p1=(440,130) p2=(440,170)
//   R2   anchor (440, 190) → p1=(440,170) p2=(440,210)
//   GND  anchor (320, 219) → p=(320,210)
const components: ComponentInstance[] = [
  {
    // Symbol id `dc-source` matches the renderer registry; engine
    // `dc-source → voltage-source` alias (Sprint 10.1) handles the
    // stamp dispatch.
    id: "V1",
    type: "dc-source",
    position: { x: 200, y: 140 },
    rotation: 0,
    flipped: false,
    label: "V1",
    params: { voltage: 12 },
    terminals: [
      { terminalId: "pos", netId: "net_vcc", position: { x: 200, y: 130 } },
      { terminalId: "neg", netId: "net_gnd", position: { x: 200, y: 150 } },
    ],
  },
  {
    id: "R1",
    type: "resistor",
    position: { x: 440, y: 150 },
    rotation: 90,
    flipped: false,
    label: "R1",
    params: { resistance: 10000 },
    terminals: [
      { terminalId: "p1", netId: "net_vcc", position: { x: 440, y: 130 } },
      { terminalId: "p2", netId: "net_mid", position: { x: 440, y: 170 } },
    ],
  },
  {
    id: "R2",
    type: "resistor",
    position: { x: 440, y: 190 },
    rotation: 90,
    flipped: false,
    label: "R2",
    params: { resistance: 4700 },
    terminals: [
      { terminalId: "p1", netId: "net_mid", position: { x: 440, y: 170 } },
      { terminalId: "p2", netId: "net_gnd", position: { x: 440, y: 210 } },
    ],
  },
  {
    // Ground anchor offset by +9 (HALF_H − pin_local_y = 13 − 4 = 9)
    // so the pin world coordinate lands EXACTLY on the gnd rail at
    // y=210.
    id: "GND1",
    type: "ground",
    position: { x: 320, y: 219 },
    rotation: 0,
    flipped: false,
    params: {},
    terminals: [{ terminalId: "p", netId: "net_gnd", position: { x: 320, y: 210 } }],
  },
];

const wires: Wire[] = [
  {
    // V1.pos and R1.p1 share y=130 — straight horizontal connection.
    id: "wire_vcc",
    netId: "net_vcc",
    style: "orthogonal",
    path: [
      { x: 200, y: 130 },
      { x: 440, y: 130 },
    ],
  },
  {
    // V1.neg drops to the gnd rail (y=210), runs across to R2.p2.
    // GND symbol's pin (320, 210) sits ON this bus at the midpoint.
    id: "wire_gnd",
    netId: "net_gnd",
    style: "orthogonal",
    path: [
      { x: 200, y: 150 },
      { x: 200, y: 210 },
      { x: 440, y: 210 },
    ],
  },
];

export const canvasDemo: Circuit = {
  schemaVersion: SCHEMA_VERSION,
  // UUID slot 25 reserved for Sprint 19 boot fixture; preset slots
  // 1-16 stay untouched.
  id: "00000000-0000-4000-8000-001900000000",
  name: "Voltage Divider",
  description: "Mockup-canonical boot fixture — V1=12V, R1=10kΩ, R2=4.7kΩ.",
  createdAt: FROZEN_TIMESTAMP,
  updatedAt: FROZEN_TIMESTAMP,
  nodes: [],
  nets,
  components,
  wires,
  groups: [],
  annotations: [],
  assertions: [],
  simulationSettings: {
    defaultAnalysis: "dc",
    dc: {
      temperature: 27,
      gmin: 1e-12,
      reltol: 1e-3,
      abstol: 1e-12,
      maxIterations: 100,
      enableDeltaVDamping: true,
      toleranceStrategy: "strict",
      enableAnderson: false,
      enableArmijo: true,
    },
    transient: {
      tstart: 0,
      tstop: 1e-3,
      method: "trap",
      reltol: 1e-3,
      abstol: 1e-12,
    },
    ac: {
      start: 1,
      stop: 1e9,
      points: 100,
      sweep: "dec",
    },
  },
  metadata: { source: "boot-fixture" },
};
